import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { ProtectedRoute } from "@/components/admin/ProtectedRoute";
import { isPreviewMode } from "@/hooks/useIsPreviewMode";
import { useWebVitals } from "@/hooks/useWebVitals";

// Eagerly load Index for fast initial paint
import Index from "./pages/Index";

// Lazy load other public pages
const Recipes = lazy(() => import("./pages/Recipes"));
const RecipeDetail = lazy(() => import("./pages/RecipeDetail"));
const Tools = lazy(() => import("./pages/Tools"));
const ToolDetail = lazy(() => import("./pages/ToolDetail"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Lazy load admin pages
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminTools = lazy(() => import("./pages/admin/AdminTools"));
const AdminRecipes = lazy(() => import("./pages/admin/AdminRecipes"));
const RecipeGenerator = lazy(() => import("./pages/admin/RecipeGenerator"));
const ResearchDashboard = lazy(() => import("./pages/admin/ResearchDashboard"));
const CtaAnalytics = lazy(() => import("./pages/admin/CtaAnalytics"));
const Login = lazy(() => import("./pages/Login"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));

const queryClient = new QueryClient();

// Check once at app initialization
const showAdminRoutes = isPreviewMode();

// Simple loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-pulse text-muted-foreground">Loading...</div>
  </div>
);

const App = () => {
  // Track Core Web Vitals in production
  useWebVitals();

  return (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<Index />} />
              <Route path="/recipes" element={<Recipes />} />
              <Route path="/recipes/:slug" element={<RecipeDetail />} />
              <Route path="/tools" element={<Tools />} />
              <Route path="/tools/:slug" element={<ToolDetail />} />
              
              {/* Admin routes - ONLY available in Lovable preview mode */}
              {showAdminRoutes && (
                <>
                  <Route path="/login" element={<Login />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                  <Route path="/admin/tools" element={<ProtectedRoute><AdminTools /></ProtectedRoute>} />
                  <Route path="/admin/recipes" element={<ProtectedRoute><AdminRecipes /></ProtectedRoute>} />
                  <Route path="/admin/generator" element={<ProtectedRoute><RecipeGenerator /></ProtectedRoute>} />
                  <Route path="/admin/research" element={<ProtectedRoute><ResearchDashboard /></ProtectedRoute>} />
                  <Route path="/admin/cta" element={<ProtectedRoute><CtaAnalytics /></ProtectedRoute>} />
                </>
              )}
              
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
